/**
 * The jQuery adapter.
 *
 * Two things make this one different from React and Vue.
 *
 * 1. **jQuery is never imported.** Not even as a type: the shapes the plugin
 *    needs are declared structurally below. jQuery is read off the global at
 *    call time, so it never enters the bundle graph, `dist/jquery.js` has no
 *    import of it, and a page that loads jQuery from a CDN after this module
 *    still works as long as it calls `registerJQueryPlugin()` itself.
 * 2. **`meta` comes from `$(el).data()`, not `el.dataset`.** That is the one
 *    place the adapter deliberately differs from the core default, and it is
 *    what a jQuery user expects: `data-count="3"` arrives as the number `3`,
 *    `data-flag="true"` as `true`, and `data-row='{"id":7}'` as an object.
 *
 * Everything else is the shared adapter core: the registration goes through
 * `attach`, so there is still exactly one delegated `contextmenu` listener on
 * the document however many elements are initialised.
 */

import { ContextMenu, stripAttachMarkers } from '../core/ContextMenu'
import type { Meta } from '../core/types'
import { bindElement, requestOpen, sharedInstance } from './shared'
import type { AdapterDefinition, DefinitionRef } from './shared'

export type { AdapterDefinition } from './shared'

/**
 * The part of a jQuery set the plugin uses.
 *
 * Declared here rather than imported from `@types/jquery` so the published
 * declaration is self contained: a consumer who has no jQuery types installed
 * can still typecheck an import of this entry.
 */
export interface JQueryCollection {
  readonly length: number
  each(callback: (index: number, element: HTMLElement) => void): JQueryCollection
  data(): Record<string, unknown>
  data(key: string): unknown
  data(key: string, value: unknown): JQueryCollection
  removeData(key: string): JQueryCollection
}

/** The part of the `jQuery` global the plugin uses. */
export interface JQueryLike {
  (target: HTMLElement | Document | string): JQueryCollection
  fn: Record<string, unknown>
}

/**
 * Anything shaped enough like jQuery to install onto.
 *
 * Deliberately looser than {@link JQueryLike}. `@types/jquery`'s `JQueryStatic`
 * has a heavily overloaded call signature and types `fn` as `JQuery`, so it
 * does not structurally satisfy the narrow interface even though it is exactly
 * what a consumer passes and works perfectly at runtime. Requiring a cast at
 * the call site would be a types problem dressed up as a consumer problem.
 */
export type JQueryInstallTarget = JQueryLike | { fn: object }

/** What `$(sel).contextMenu(config)` accepts. */
export interface JQueryContextMenuConfig extends AdapterDefinition {
  /** Register on this instance instead of the shared one. */
  instance?: ContextMenu
}

/** The commands the plugin understands in place of a config object. */
export type JQueryContextMenuCommand = 'destroy' | 'open'

/**
 * Where the disposer is kept, per element.
 *
 * `$.data` rather than a module `WeakMap` so a consumer debugging in the
 * console can see that an element is initialised. The cost is that the key
 * shows up in `$(el).data()`, which is also where `meta` comes from, so
 * `metaFor` strips it again.
 */
const DATA_KEY = 'ctxMenu'

/** A jQuery event object, which wraps the native one it was built from. */
interface JQueryEventLike {
  originalEvent?: Event | null
}

/** The native event behind whatever the caller handed the `open` command. */
function nativeEvent(source: unknown): Event | null {
  if (source instanceof Event) return source

  if (typeof source === 'object' && source !== null && 'originalEvent' in source) {
    const original = (source as JQueryEventLike).originalEvent

    return original instanceof Event ? original : null
  }

  return null
}

/** jQuery on the global, if a page or a bundle has put it there. */
function globalJQuery(): JQueryLike | null {
  const scope = globalThis as { jQuery?: unknown; $?: unknown }
  const found = scope.jQuery ?? scope.$

  if (found === null || found === undefined) return null
  if (typeof found !== 'function' && typeof found !== 'object') return null
  if (!('fn' in found)) return null

  return found as JQueryLike
}

/**
 * The element's jQuery data, as the menu's `meta`.
 *
 * A copy, for two reasons: `.data()` hands back jQuery's own store, so a
 * predicate that writes to `meta` would otherwise be writing into it, and the
 * library's own bookkeeping keys have no business reaching a predicate. The
 * `attach` marker is one of those keys, and it is filtered by the core's own
 * `stripAttachMarkers` rather than by a second copy of the rule here.
 */
function metaFor(jq: JQueryLike, el: HTMLElement): Meta {
  const data: Record<string, unknown> = { ...jq(el).data() }

  delete data[DATA_KEY]

  return stripAttachMarkers(data)
}

/** Dispose whatever this element already had, if anything. */
function disposeElement(jq: JQueryLike, el: HTMLElement): void {
  const stored = jq(el).data(DATA_KEY)

  if (typeof stored === 'function') (stored as () => void)()

  jq(el).removeData(DATA_KEY)
}

function initElement(jq: JQueryLike, el: HTMLElement, config: JQueryContextMenuConfig): void {
  // Re-initialising a re-rendered list must not stack handlers, so the old
  // registration goes before the new one is made.
  disposeElement(jq, el)

  const { instance, ...rest } = config
  const definition: AdapterDefinition = { ...rest }

  // The jQuery default, installed only when the caller expressed no preference.
  // An explicit `meta` goes down the shared `WeakMap` path instead, so it
  // reaches predicates as the object it is.
  if (definition.resolveMeta === undefined && definition.meta === undefined) {
    definition.resolveMeta = (element): Meta => metaFor(jq, element)
  }

  const live: DefinitionRef = { current: definition }
  const target = instance ?? sharedInstance()

  // Null only where there is no document at all. jQuery cannot have produced a
  // collection of elements in that case, so this is unreachable in practice and
  // is here so the null the shared instance can return is handled rather than
  // asserted away.
  if (target === null) return

  jq(el).data(DATA_KEY, bindElement(target, el, live))
}

/**
 * Build the `$.fn.contextMenu` method bound to one jQuery.
 *
 * Closing over `jq` rather than re-reading the global keeps a page that has
 * two jQuery copies honest: the plugin installed onto one uses that one.
 */
function pluginMethod(jq: JQueryLike) {
  return function contextMenu(
    this: JQueryCollection,
    config?: JQueryContextMenuConfig | JQueryContextMenuCommand,
    event?: unknown,
  ): JQueryCollection {
    if (config === 'destroy') {
      this.each((_index, element) => {
        disposeElement(jq, element)
      })

      return this
    }

    if (config === 'open') {
      const source = nativeEvent(event)

      this.each((_index, element) => {
        requestOpen(element, source)
      })

      return this
    }

    if (typeof config !== 'object' || config === null) {
      console.warn(
        `[context-menu] $().contextMenu was called with ${String(config)}; expected a config object, "destroy" or "open"`,
      )

      return this
    }

    this.each((_index, element) => {
      initElement(jq, element, config)
    })

    return this
  }
}

/**
 * Install `$.fn.contextMenu`.
 *
 * Called for you when this module is imported into a page that already has
 * jQuery on the global. Call it yourself when jQuery arrives afterwards, or to
 * install onto a jQuery that is not on the global at all.
 *
 * With no jQuery to install onto it warns and returns; it never throws, so a
 * bundle that includes this entry does not break a page that dropped jQuery.
 */
export function registerJQueryPlugin(jq?: JQueryInstallTarget): void {
  const target = (jq as JQueryLike | undefined) ?? globalJQuery()

  if (target === null) {
    console.warn(
      '[context-menu] no jQuery found on the global, so $.fn.contextMenu was not installed',
    )

    return
  }

  target.fn['contextMenu'] = pluginMethod(target)
}

// Self registration, silent when there is nothing to register onto. Silent and
// not warning, because importing this entry from a bundle before jQuery has
// loaded is legitimate: that consumer calls `registerJQueryPlugin()` later, and
// a warning on every import would be noise rather than a signal.
const present = globalJQuery()

if (present !== null) registerJQueryPlugin(present)

declare global {
  interface JQuery<TElement = HTMLElement> {
    /** Initialise a context menu on every element in the set. */
    contextMenu(config: JQueryContextMenuConfig): JQuery<TElement>
    /** Remove the registration from every element in the set. */
    contextMenu(command: 'destroy'): JQuery<TElement>
    /** Open the menu, optionally at a native or jQuery event's coordinates. */
    contextMenu(command: 'open', event?: unknown): JQuery<TElement>
  }
}
